import { useEffect, useState } from 'react';
import db from '@/db';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { refreshDeviceNicknameCache, subscribeSyncState } from '@/lib/firebaseSync';
import { Smartphone, Save, FlaskConical, CheckCircle2, Copy } from 'lucide-react';

const PILOT_CREDS = {
  // Tokens de teste PÚBLICOS do Mercado Pago (documentação oficial).
  // Válidos apenas em sandbox; não movimentam dinheiro real.
  mpAccessToken: 'TEST-3400000000000000-000000-00000000000000000000000000000000-000000000',
  mpEnvironment: 'sandbox',
  // Z-API não tem sandbox público gratuito — fica em MOCK identificável.
  zapiInstanceId: 'PILOT-MOCK-INSTANCE',
  zapiInstanceToken: 'PILOT-MOCK-TOKEN-VERY-LONG-STRING-FOR-TESTING-ONLY',
  zapiClientToken: '',
};

export default function DeviceAndPilotCard() {
  const [nickname, setNickname] = useState('');
  const [pilotMode, setPilotMode] = useState(false);
  const [uid, setUid] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    const [n, p, u] = await Promise.all([
      db.config.get('deviceNickname'),
      db.config.get('pilotMode'),
      db.config.get('deviceUid'),
    ]);
    setNickname(n?.value || '');
    setPilotMode(p?.value === true);
    setUid(u?.value || null);
  };

  useEffect(() => {
    reload();
    const unsub = subscribeSyncState((s) => { if (s.uid) setUid(s.uid); });
    return unsub;
  }, []);

  const saveNickname = async () => {
    const trimmed = nickname.trim().slice(0, 40);
    setSaving(true);
    try {
      await db.config.put({ key: 'deviceNickname', value: trimmed });
      await refreshDeviceNicknameCache();
      toast.success(trimmed ? `Apelido salvo: ${trimmed}` : 'Apelido removido');
    } finally {
      setSaving(false);
    }
  };

  const togglePilot = async (on) => {
    setPilotMode(on);
    await db.config.put({ key: 'pilotMode', value: on });
    if (on) {
      await db.config.bulkPut([
        { key: 'mpAccessToken', value: PILOT_CREDS.mpAccessToken },
        { key: 'mpEnvironment', value: PILOT_CREDS.mpEnvironment },
        { key: 'zapiInstanceId', value: PILOT_CREDS.zapiInstanceId },
        { key: 'zapiInstanceToken', value: PILOT_CREDS.zapiInstanceToken },
        { key: 'zapiClientToken', value: PILOT_CREDS.zapiClientToken },
      ]);
      toast.success('🧪 Modo Piloto ativado', {
        description: 'Credenciais sandbox injetadas. Pix e WhatsApp em modo demo — recarregue o /config para ver.',
        duration: 5000,
      });
    } else {
      // Desliga: limpa as credenciais de teste (se ainda forem as placeholders)
      const [mp, zap] = await Promise.all([
        db.config.get('mpAccessToken'),
        db.config.get('zapiInstanceToken'),
      ]);
      const ops = [];
      if (mp?.value === PILOT_CREDS.mpAccessToken) ops.push({ key: 'mpAccessToken', value: '' });
      if (zap?.value === PILOT_CREDS.zapiInstanceToken) {
        ops.push({ key: 'zapiInstanceId', value: '' });
        ops.push({ key: 'zapiInstanceToken', value: '' });
      }
      if (ops.length) await db.config.bulkPut(ops);
      toast('Modo Piloto desativado — credenciais sandbox removidas.');
    }
  };

  const copyUid = () => {
    if (!uid) return;
    try {
      navigator.clipboard.writeText(uid);
      toast.success('UID copiado para a área de transferência');
    } catch {
      toast.error('Não foi possível copiar');
    }
  };

  return (
    <section data-testid="device-pilot-card" className="bg-white border border-slate-200 rounded-2xl p-6 space-y-6">
      {/* === Identidade do Dispositivo === */}
      <div className="space-y-3">
        <header className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 text-white flex items-center justify-center">
            <Smartphone size={14} strokeWidth={2.25} />
          </div>
          <div>
            <p className="font-heading font-bold text-slate-900 text-base">Este Dispositivo</p>
            <p className="text-[11px] text-slate-500">
              O apelido aparece no Log de Auditoria e fica gravado em cada ação no Firebase.
            </p>
          </div>
        </header>

        <div>
          <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 mb-1 block">
            Apelido (ex: Tablet Cozinha, Celular do João)
          </label>
          <div className="flex gap-2">
            <Input
              data-testid="device-nickname-input"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="Tablet Cozinha"
              maxLength={40}
              className="flex-1 h-10 border-2 border-slate-200 rounded-lg"
            />
            <button
              data-testid="save-device-nickname-btn"
              onClick={saveNickname}
              disabled={saving}
              className="px-4 h-10 rounded-lg bg-sky-600 text-white text-sm font-bold hover:bg-sky-700 disabled:opacity-50 flex items-center gap-1.5"
            >
              <Save size={13} /> Salvar
            </button>
          </div>
        </div>

        {uid && (
          <div data-testid="device-uid-display" className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">UID</span>
            <code className="font-mono text-[11px] text-slate-700 truncate flex-1">{uid}</code>
            <button
              onClick={copyUid}
              className="w-7 h-7 rounded-md text-slate-500 hover:bg-slate-200 flex items-center justify-center"
              title="Copiar UID"
            >
              <Copy size={12} />
            </button>
          </div>
        )}
      </div>

      <div className="border-t border-slate-100" />

      {/* === Modo Piloto Sandbox === */}
      <div className="space-y-3">
        <header className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white flex items-center justify-center">
            <FlaskConical size={14} strokeWidth={2.25} />
          </div>
          <div>
            <p className="font-heading font-bold text-slate-900 text-base">Modo Piloto Sandbox</p>
            <p className="text-[11px] text-slate-500">
              Ativa credenciais de teste para demonstrar Pix + WhatsApp sem conta real. Ideal para apresentar o Kaven a parceiros.
            </p>
          </div>
        </header>

        <label className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${
          pilotMode
            ? 'border-violet-400 bg-violet-50/70'
            : 'border-slate-200 bg-white hover:border-slate-400'
        }`}>
          <div>
            <p className={`text-sm font-bold ${pilotMode ? 'text-violet-900' : 'text-slate-800'}`}>
              {pilotMode ? '🧪 Modo Piloto ATIVO' : 'Ativar Modo Piloto'}
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {pilotMode
                ? 'Pix sandbox (MP TEST token) + Z-API MOCK. Nenhum cliente real é cobrado ou notificado.'
                : 'Pré-popula credenciais de demo em Mercado Pago + Z-API.'}
            </p>
          </div>
          <Switch
            data-testid="pilot-mode-toggle"
            checked={pilotMode}
            onCheckedChange={togglePilot}
          />
        </label>

        {pilotMode && (
          <div className="bg-violet-50/50 border border-violet-200 rounded-lg p-3 space-y-1">
            <p className="text-[11px] font-bold text-violet-800 flex items-center gap-1.5">
              <CheckCircle2 size={12} /> Credenciais sandbox injetadas:
            </p>
            <ul className="text-[11px] text-violet-700 font-mono space-y-0.5 ml-5">
              <li>• Mercado Pago: <strong>TEST-340...</strong> (sandbox)</li>
              <li>• Z-API: <strong>PILOT-MOCK</strong> (modo mock identificável)</li>
            </ul>
            <p className="text-[11px] text-violet-600/80 mt-1.5">
              Recarregue <code className="bg-violet-100 px-1 rounded">/config</code> para ver as keys preenchidas.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
